import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { AnyFunction, isFunction } from '@mews/std';
import { getRepositoryQueryKey } from './getRepositoryQueryKey';

export const createUseRepositoryQuery =
    <RepositoryRegistry>(useRepositoryRegistry: () => RepositoryRegistry) =>
    <
        RepositoryName extends keyof RepositoryRegistry,
        MethodName extends keyof RepositoryRegistry[RepositoryName],
        Method extends RepositoryRegistry[RepositoryName][MethodName] extends AnyFunction
            ? RepositoryRegistry[RepositoryName][MethodName]
            : never,
        Data = Awaited<ReturnType<Method>>,
    >(
        repositoryName: RepositoryName,
        methodName: MethodName,
        params: Parameters<Method>[0],
        queryOptions?: Omit<UseQueryOptions<Awaited<ReturnType<Method>>, Error, Data>, 'queryKey' | 'queryFn'>
    ) => {
        const registry = useRepositoryRegistry();
        return useQuery({
            queryKey: getRepositoryQueryKey<RepositoryRegistry, RepositoryName, MethodName, typeof params>(
                repositoryName,
                methodName,
                params
            ),

            queryFn: async ({ signal }): Promise<Awaited<ReturnType<Method>>> => {
                const method = registry[repositoryName][methodName];
                return isFunction(method) ? method(params, { signal }) : await Promise.reject();
            },
            ...queryOptions,
        });
    };
